import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import api from '../../utils/api';

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

const STRIPE_APPEARANCE = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#3F51B5',
    borderRadius: '8px',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  }
};

const TYPE_LABELS = {
  long_course: 'Long Course',
  block: 'Block Course',
  workshop: 'Workshop'
};

const LOCATION_LABELS = {
  online: 'Online',
  in_person: 'In-Person',
  hybrid: 'Hybrid'
};

const TZ_SHORT = {
  'America/New_York': 'ET',
  'America/Chicago': 'CT',
  'America/Denver': 'MT',
  'America/Los_Angeles': 'PT',
  'Asia/Kolkata': 'IST'
};

/* ─── Inner payment form (must be inside <Elements>) ──────────── */
const CheckoutForm = ({ enrollmentId, totalAmount, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href
      },
      redirect: 'if_required'
    });

    if (error) {
      onError(error.message);
      setProcessing(false);
    } else if (paymentIntent) {
      if (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing') {
        const isProcessing = paymentIntent.status === 'processing';
        try {
          const { data } = await api.post('/enroll/confirm-payment', { enrollmentId });
          onSuccess({ ...data, processing: isProcessing });
        } catch (err) {
          if (isProcessing) {
            // Bank transfer: payment is still processing, webhook will handle enrollment
            onSuccess({ processing: true });
          } else {
            // Card payment succeeded but confirm API failed — webhook will handle
            onSuccess({ processing: false });
          }
        }
      }
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement options={{ layout: 'tabs' }} />
      <button
        type="submit"
        disabled={!stripe || processing}
        className="w-full mt-6 bg-brand-500 text-white py-3.5 rounded-lg text-lg font-semibold hover:bg-brand-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
      >
        {processing ? 'Processing payment...' : `Enroll & Pay $${totalAmount}`}
      </button>
    </form>
  );
};

/* ─── Main enrollment page ─────────────────────────────────────── */
const EnrollmentPage = () => {
  const { slug, courseId } = useParams();
  const { isAuthenticated, login, user, refreshUser } = useAuth();

  // Data
  const [studioData, setStudioData] = useState(null);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(0);

  // Auth
  const authLoading = false; // redirect-based auth, no loading state needed

  // Payment
  const [clientSecret, setClientSecret] = useState(null);
  const [enrollmentId, setEnrollmentId] = useState(null);
  const [enrollmentTotal, setEnrollmentTotal] = useState(null);
  const [isEnrollmentWithAdvance, setIsEnrollmentWithAdvance] = useState(false);
  const [creatingPayment, setCreatingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  /* ── Fetch course data ── */
  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const { data } = await api.get(`/widget/${slug}/courses`);
        setStudioData(data.studio);
        const found = data.courses.find(c => c._id === courseId);
        if (found) {
          setCourse(found);
        } else {
          setError('Course not found or not currently available.');
        }
      } catch (err) {
        setError('Studio not found.');
      } finally {
        setLoading(false);
      }
    };
    fetchCourse();
  }, [slug, courseId]);

  // Set page title + favicon based on studio
  useEffect(() => {
    if (studioData?.name) {
      document.title = studioData.name;
    }
    if (studioData?.branding?.logo) {
      const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
      link.rel = 'icon';
      link.href = studioData.branding.logo;
      document.head.appendChild(link);
    }
  }, [studioData]);

  /* ── Create payment intent ── */
  const createPaymentIntent = useCallback(async () => {
    if (!course || creatingPayment) return;
    setCreatingPayment(true);
    setError('');
    try {
      const { data } = await api.post('/enroll/create-payment', {
        courseId: course._id,
        billingPlanIndex: selectedPlan
      });
      setClientSecret(data.clientSecret);
      setEnrollmentId(data.enrollmentId);
      if (data.isEnrollmentWithAdvance) {
        setEnrollmentTotal(data.enrollmentTotal);
        setIsEnrollmentWithAdvance(true);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to initialize payment');
    } finally {
      setCreatingPayment(false);
    }
  }, [course, selectedPlan, creatingPayment]);

  // Auto-create payment intent when authenticated
  useEffect(() => {
    if (isAuthenticated && course && !clientSecret && !paymentSuccess && !creatingPayment) {
      createPaymentIntent();
    }
  }, [isAuthenticated, course, clientSecret, paymentSuccess, creatingPayment, createPaymentIntent]);

  /* ── Auth handlers (redirect-based, not popup) ── */
  const handleSignUp = () => {
    sessionStorage.setItem('dancehall_signup_role', 'student');
    login({
      authorizationParams: { screen_hint: 'signup' },
      appState: { returnTo: window.location.pathname }
    });
  };

  const handleLogin = () => {
    sessionStorage.setItem('dancehall_signup_role', 'student');
    login({
      appState: { returnTo: window.location.pathname }
    });
  };

  /* ── Plan change resets payment ── */
  const handlePlanChange = (idx) => {
    setSelectedPlan(idx);
    if (clientSecret) {
      setClientSecret(null);
      setEnrollmentId(null);
      setEnrollmentTotal(null);
      setIsEnrollmentWithAdvance(false);
    }
  };

  /* ── Payment callbacks ── */
  const handlePaymentSuccess = async (data) => {
    if (data?.processing) {
      setPaymentProcessing(true);
    }
    setPaymentSuccess(true);
    // Refresh user data so studioId is set (webhook may take a moment)
    setTimeout(async () => {
      try { await refreshUser(); } catch (e) { /* ok */ }
    }, 2000);
  };

  const handlePaymentError = (message) => {
    setError(message);
  };

  /* ── Loading / Error states ── */
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-400 text-sm">Loading course...</div>
      </div>
    );
  }

  if (error && !course) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Not Available</h2>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  const plan = course.billingPlans?.[selectedPlan];
  const studentPrice = plan?.studentPrice || plan?.amount || 0;
  const tz = TZ_SHORT[course.schedule?.timezone] || course.schedule?.timezone || '';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <h2 className="text-lg font-bold text-gray-900">{studioData?.name}</h2>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-8">
        {paymentSuccess ? (
          /* ── Success state ── */
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
            <div className="text-5xl mb-4">&#9989;</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">You're enrolled!</h1>
            <p className="text-lg text-gray-700 font-medium mb-3">{course.name}</p>

            {/* Schedule info */}
            {course.schedule?.days?.length > 0 && (
              <p className="text-sm text-gray-600 mb-1">
                {course.schedule.days.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')}
                {(course.schedule.startTime || course.schedule.endTime) && (
                  <span>
                    {' \u00B7 '}
                    {course.schedule.startTime}
                    {course.schedule.endTime ? ` \u2013 ${course.schedule.endTime}` : ''}
                    {tz && <span className="text-gray-400 ml-1">{tz}</span>}
                  </span>
                )}
              </p>
            )}

            {paymentProcessing ? (
              <div className="mt-4 mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-700">
                  Your bank transfer is processing. Your enrollment will be confirmed once payment clears (1-3 business days).
                </p>
              </div>
            ) : (
              <div className="mt-4 mb-6 space-y-2">
                <p className="text-sm text-gray-600">
                  You'll receive an email with your class schedule and what to expect.
                </p>
                <p className="text-sm text-gray-600">
                  Log in to your dashboard to see upcoming classes, access materials, and track your progress.
                </p>
              </div>
            )}

            <Link
              to="/student"
              className="inline-block bg-brand-500 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-brand-600 transition-colors"
            >
              Go to Dashboard
            </Link>
          </div>
        ) : (
          /* ── Enrollment form ── */
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">

            {/* Course Info */}
            <div className="p-6">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium text-brand-500 bg-brand-50 px-2 py-0.5 rounded">
                  {TYPE_LABELS[course.type] || course.type}
                </span>
                {course.location?.type && (
                  <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                    {LOCATION_LABELS[course.location.type]}
                  </span>
                )}
              </div>

              <h1 className="text-2xl font-bold text-gray-900 mt-3">{course.name}</h1>

              {(course.danceStyle || course.levelName) && (
                <p className="text-gray-500 mt-1">
                  {course.danceStyle}{course.danceStyle && course.levelName ? ' \u00B7 ' : ''}{course.levelName}
                </p>
              )}

              {course.description && (
                <p className="text-gray-600 mt-3 text-sm leading-relaxed">{course.description}</p>
              )}

              {/* Class Details */}
              <div className="mt-5 bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                {course.schedule?.days?.length > 0 && (
                  <div className="flex items-start gap-3">
                    <span className="text-gray-400 mt-0.5">&#x1F4C5;</span>
                    <div>
                      <div className="font-medium text-gray-900">
                        {course.schedule.days.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')}
                      </div>
                      {(course.schedule.startTime || course.schedule.endTime) && (
                        <div className="text-gray-600">
                          {course.schedule.startTime}
                          {course.schedule.endTime ? ` \u2013 ${course.schedule.endTime}` : ''}
                          {tz && <span className="text-gray-400 ml-1">{tz}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {course.type === 'workshop' && course.schedule?.sessionDate && (
                  <div className="flex items-start gap-3">
                    <span className="text-gray-400 mt-0.5">&#x1F4C5;</span>
                    <div>
                      <div className="font-medium text-gray-900">
                        {new Date(course.schedule.sessionDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                      </div>
                      {(course.schedule.sessionStartTime || course.schedule.sessionEndTime) && (
                        <div className="text-gray-600">
                          {course.schedule.sessionStartTime}
                          {course.schedule.sessionEndTime ? ` \u2013 ${course.schedule.sessionEndTime}` : ''}
                          {tz && <span className="text-gray-400 ml-1">{tz}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {course.type === 'block' && course.schedule?.blockWeeks && (
                  <div className="flex items-start gap-3">
                    <span className="text-gray-400 mt-0.5">&#x23F3;</span>
                    <div className="text-gray-600">
                      {course.schedule.blockWeeks} weeks
                      {course.schedule.blockStartDate && (
                        <span> &middot; Starts {new Date(course.schedule.blockStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      )}
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <span className="text-gray-400 mt-0.5">&#x1F4CD;</span>
                  <div>
                    <div className="text-gray-900">{LOCATION_LABELS[course.location?.type] || 'In-Person'}</div>
                    {course.location?.address && (
                      <div className="text-gray-500">{course.location.address}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Plan Selector */}
            {course.billingPlans?.length > 0 && (
              <div className="border-t border-gray-100 p-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Choose a plan</h3>
                <div className="space-y-2">
                  {course.billingPlans.map((p, i) => {
                    const price = p.studentPrice || p.amount;
                    const freqLabel = { monthly: '/mo', quarterly: '/quarter', semi_annual: '/6 mo', annual: '/yr', one_time: '' };
                    const freq = freqLabel[p.frequency] || '';
                    return (
                      <label
                        key={i}
                        className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                          selectedPlan === i
                            ? 'border-brand-500 bg-brand-50/50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            name="plan"
                            checked={selectedPlan === i}
                            onChange={() => handlePlanChange(i)}
                            className="accent-brand-500"
                          />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{p.name}</div>
                            <div className="text-xs text-gray-500">{p.frequency.replace('_', ' ')}</div>
                          </div>
                        </div>
                        <div className="text-lg font-bold text-gray-900">${price}{freq}</div>
                      </label>
                    );
                  })}
                </div>

                {/* Enrollment fee breakdown for selected plan */}
                {plan?.enrollmentPrice && plan.frequency !== 'one_time' && (
                  <div className="mt-4 bg-gray-50 rounded-lg p-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-500">First {plan.frequency.replace('_', ' ')}</span>
                      <span className="text-gray-900">${plan.studentPrice}</span>
                    </div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-500">Enrollment fee <span className="text-xs">(applied to last month)</span></span>
                      <span className="text-gray-900">${plan.advanceDeposit}</span>
                    </div>
                    <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
                      <span className="font-medium text-gray-700">Due today</span>
                      <span className="font-bold text-gray-900">${plan.enrollmentPrice}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">Then ${plan.studentPrice}/{plan.frequency === 'monthly' ? 'mo' : plan.frequency.replace('_', ' ')}</p>
                  </div>
                )}
              </div>
            )}

            {/* Auth + Payment Section */}
            <div className="border-t border-gray-100 p-6">
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                  <button onClick={() => setError('')} className="ml-2 text-red-500 hover:text-red-700 font-bold">&times;</button>
                </div>
              )}

              {!isAuthenticated ? (
                /* ── Not logged in ── */
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Create an account to enroll</h3>
                  <div className="flex gap-3">
                    <button
                      onClick={handleSignUp}
                      disabled={authLoading}
                      className="flex-1 bg-brand-500 text-white py-3 rounded-lg font-semibold hover:bg-brand-600 disabled:bg-gray-300 transition-colors"
                    >
                      {authLoading ? 'Opening...' : 'Sign Up'}
                    </button>
                    <button
                      onClick={handleLogin}
                      disabled={authLoading}
                      className="flex-1 border-2 border-brand-500 text-brand-500 py-3 rounded-lg font-semibold hover:bg-brand-50 disabled:border-gray-300 disabled:text-gray-400 transition-colors"
                    >
                      Log In
                    </button>
                  </div>
                </div>
              ) : (
                /* ── Logged in ── */
                <div>
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center text-brand-600 text-sm font-bold">
                        {(user?.name || user?.email || '?').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user?.name || 'Student'}</div>
                        <div className="text-xs text-gray-500">{user?.email}</div>
                      </div>
                    </div>
                    <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded font-medium">Signed in</span>
                  </div>

                  {/* Payment Form */}
                  {creatingPayment && (
                    <div className="text-center py-8">
                      <div className="text-gray-400 text-sm">Setting up payment...</div>
                    </div>
                  )}

                  {clientSecret && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">Payment method</h3>
                      <Elements
                        stripe={stripePromise}
                        options={{ clientSecret, appearance: STRIPE_APPEARANCE }}
                      >
                        <CheckoutForm
                          enrollmentId={enrollmentId}
                          totalAmount={isEnrollmentWithAdvance ? enrollmentTotal : studentPrice}
                          onSuccess={handlePaymentSuccess}
                          onError={handlePaymentError}
                        />
                      </Elements>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <p className="text-center text-xs text-gray-400 mt-6">
          Powered by DanceHall Studios
        </p>
      </div>
    </div>
  );
};

export default EnrollmentPage;
